import React, { useState, useEffect } from "react";
import { Button, IconButton, useMediaQuery } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { useEnumStrings } from "../enumStrings";
import theme from "../../themes";
import EditOrangeIcon from "../../images/edit-icon-orange.png";
import TrashIcon from "../../images/trash.png";
import UploadRoundIcon from "../../images/upload-rounded.png";
import BannerUploadModal from "../pages/ChamberAdmin/BannerUploadModal";
import BannerPreviewModal from "../pages/ChamberAdmin/BannerPreviewModal";
import DeleteBannerModal from "../pages/ChamberAdmin/DeleteBannerModal";
import PerksBannerPhoneMockup from "../../images/perksbanner.jpeg";
import VideoIcon from "../../images/lets-icons_video.png";

interface PerkBannerSectionProps {
    isFeaturedMember: boolean;
    formik: any;
    isCreateMode: boolean;
    isChamberAdmin?: boolean;
}

const PerkBannerSection: React.FC<PerkBannerSectionProps> = ({
    isFeaturedMember,
    formik,
    isCreateMode,
    isChamberAdmin = false,
}) => {
    const enumStrings = useEnumStrings();
    const isMobile = useMediaQuery("(max-width: 600px)");
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [activeBannerIndex, setActiveBannerIndex] = useState(1);
    const [bannerPreviewOpen, setBannerPreviewOpen] = useState(false);
    const [deleteBannerPosition, setDeleteBannerPosition] = useState<number | null>(null);

    // Normalize incoming banner positions to contiguous 1-indexed values so the
    // slot lookup works regardless of whether the data was saved by the member
    // side (1-indexed) or the chamber admin side (AddMemberBanners, 0-indexed).
    useEffect(() => {
        const banners = formik.values.banner || [];
        if (banners.length === 0) return;
        const sorted = [...banners]
            .filter((b: any) => b?.contentUrl)
            .sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0));
        const needsReindex = sorted.some((b: any, i: number) => b.position !== i + 1);
        if (!needsReindex) return;
        const reindexed = sorted.map((b: any, i: number) => ({ ...b, position: i + 1 }));
        formik.setFieldValue("banner", reindexed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.banner]);

    const footerText = isCreateMode
        ? enumStrings.editMemberForm.bannerPreviewFooterCreate
        : enumStrings.editMemberForm.bannerPreviewFooter;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "36px", width: "100%", padding: "0" }}>
            {/* Activated / Not Activated Badge */}
            <div
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isFeaturedMember ? "#00B621" : theme.palette.text.primary,
                    borderRadius: "30px",
                    padding: isMobile ? "10px 14px" : "14px 15px",
                    height: isMobile ? "auto" : "52px",
                    boxSizing: "border-box",
                    alignSelf: "flex-start",
                }}
            >
                <span
                    style={{
                        fontWeight: 700,
                        fontSize: isMobile ? "13px" : "20px",
                        lineHeight: isMobile ? "16px" : "24px",
                        color: "#FFFFFF",
                    }}
                >
                    {isFeaturedMember
                        ? enumStrings.editMemberForm.perkBannersActivatedTitle
                        : enumStrings.editMemberForm.perkBannersNotActivatedTitle}
                </span>
            </div>

            {isFeaturedMember ? (
                <>
                    <p
                        style={{
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#000000",
                            margin: 0,
                        }}
                        dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.featuredMemberUpgradedDesc }}
                    />
                    <p
                        style={{
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#000000",
                            margin: 0,
                        }}
                        dangerouslySetInnerHTML={{ __html: enumStrings.editMemberForm.bannerQualityRecommendation }}
                    />
                </>
            ) : (
                <>
                    <p
                        style={{
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#000000",
                            margin: 0,
                        }}
                    >
                        {enumStrings.createMemberForm.featuredMemberBannersDesc}
                    </p>
                    {isChamberAdmin ? (
                        <p
                            style={{
                                fontWeight: 400,
                                fontSize: "16px",
                                lineHeight: "24px",
                                color: "#000000",
                                margin: 0,
                            }}
                        >
                            {enumStrings.editMemberForm.perkBannersNotActivatedDesc}
                        </p>
                    ) : (
                        <p
                            style={{
                                fontWeight: 400,
                                fontSize: "16px",
                                lineHeight: "24px",
                                color: "#000000",
                                margin: 0,
                                textAlign: "center",
                            }}
                        >
                            {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                .split("{notUpgraded}")[0]}
                            <span style={{ fontWeight: 700 }}>{enumStrings.createMemberForm.notUpgradedBold}</span>
                            {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                .split("{notUpgraded}")[1]
                                ?.split("{clickHere}")[0]}
                            <span
                                style={{
                                    color: "#E1A731",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontWeight: 700,
                                }}
                            >
                                {enumStrings.createMemberForm.clickHereLink}
                            </span>
                            {enumStrings.createMemberForm.featuredMemberNotUpgraded
                                .split("{clickHere}")[1]}
                        </p>
                    )}
                </>
            )}

            {/* Banner Labels + Cards Container */}
            <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
                gap: isMobile ? "16px 12px" : "14px 12px",
                width: "100%",
                ...(isFeaturedMember ? {} : { pointerEvents: "none" as const }),
            }}>
                    {[1, 2, 3, 4].map((position) => {
                        const banner = formik.values.banner?.find((b: any) => b.position === position);
                        const hasContent = banner?.contentUrl;
                        const highestPosition = Math.max(...(formik.values.banner || []).map((b: any) => b.position), 0);

                        return (
                            <div key={position} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                {/* Label */}
                                <p style={{
                                    margin: 0,
                                    fontWeight: 600,
                                    fontSize: isMobile ? "15px" : "20px",
                                    lineHeight: "24px",
                                    textAlign: "center",
                                    color: isFeaturedMember ? theme.palette.primary.main : "#888888",
                                    width: "100%",
                                }}>
                                    {enumStrings.editMemberForm.bannerLabel} {position}
                                </p>
                                {hasContent ? (
                                    <div
                                        style={{
                                            backgroundColor: "#FFFFFF",
                                            border: `2px solid ${theme.palette.primary.main}`,
                                            borderRadius: "10px",
                                            overflow: "hidden",
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "100%",
                                            height: "175px",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                overflow: "hidden",
                                                position: "relative",
                                                padding: "12px",
                                                flex: 1,
                                            }}
                                        >
                                            {banner?.contentType === "IMAGE" ? (
                                                <img
                                                    src={banner.contentUrl}
                                                    alt={`Banner ${position}`}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: theme.palette.primary.main,
                                                        fontSize: "20px",
                                                        fontWeight: 600,
                                                        gap: "8px",
                                                        border: `1px solid ${theme.palette.primary.main}`,
                                                        borderRadius: "4px",
                                                        boxSizing: "border-box",
                                                    }}
                                                >
                                                    <img src={VideoIcon} alt="Video" style={{ width: "49px", height: "49px" }} />
                                                    {enumStrings.editMemberForm?.watchVideo || "Watch Video"}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "6px 12px",
                                            }}
                                        >
                                            <div style={{ display: "flex", gap: "8px" }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setActiveBannerIndex(position);
                                                        setBannerModalOpen(true);
                                                    }}
                                                    style={{
                                                        backgroundColor: "#E3E3E3",
                                                        borderRadius: "5px",
                                                        width: "36px",
                                                        height: "33px",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px", filter: "brightness(0)" }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteBannerPosition(position)}
                                                    style={{
                                                        backgroundColor: (theme.palette as any).delete.main,
                                                        borderRadius: "5px",
                                                        width: "36px",
                                                        height: "33px",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                                                </IconButton>
                                            </div>
                                            <div style={{ display: "flex", gap: "4px" }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (position > 1) {
                                                            const currentBanners = [...(formik.values.banner || [])];
                                                            const thisIdx = currentBanners.findIndex((b: any) => b.position === position);
                                                            const prevIdx = currentBanners.findIndex((b: any) => b.position === position - 1);
                                                            if (thisIdx >= 0 && prevIdx >= 0) {
                                                                currentBanners[thisIdx].position = position - 1;
                                                                currentBanners[prevIdx].position = position;
                                                                formik.setFieldValue("banner", [...currentBanners]);
                                                            }
                                                        }
                                                    }}
                                                    disabled={position === 1}
                                                    style={{
                                                        backgroundColor: position === 1 ? "#D9D9D9" : theme.palette.primary.main,
                                                        color: position === 1 ? "#666" : "white",
                                                        borderRadius: "5px",
                                                        minWidth: "29px",
                                                        width: "29px",
                                                        height: "26px",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (position < highestPosition) {
                                                            const currentBanners = [...(formik.values.banner || [])];
                                                            const thisIdx = currentBanners.findIndex((b: any) => b.position === position);
                                                            const nextIdx = currentBanners.findIndex((b: any) => b.position === position + 1);
                                                            if (thisIdx >= 0 && nextIdx >= 0) {
                                                                currentBanners[thisIdx].position = position + 1;
                                                                currentBanners[nextIdx].position = position;
                                                                formik.setFieldValue("banner", [...currentBanners]);
                                                            }
                                                        }
                                                    }}
                                                    disabled={position === 4 || position >= highestPosition}
                                                    style={{
                                                        backgroundColor: (position === 4 || position >= highestPosition) ? "#D9D9D9" : theme.palette.primary.main,
                                                        color: (position === 4 || position >= highestPosition) ? "#666" : "white",
                                                        borderRadius: "5px",
                                                        minWidth: "29px",
                                                        width: "29px",
                                                        height: "26px",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => {
                                            if (isFeaturedMember) {
                                                setActiveBannerIndex(position);
                                                setBannerModalOpen(true);
                                            }
                                        }}
                                        style={{
                                            backgroundColor: isFeaturedMember ? "#FFFFFF" : "#9E9E9E",
                                            border: isFeaturedMember ? `2px solid ${theme.palette.primary.main}` : "none",
                                            borderRadius: "10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "100%",
                                            height: isMobile ? "90px" : "108px",
                                            boxSizing: "border-box",
                                            cursor: isFeaturedMember ? "pointer" : "default",
                                            padding: isMobile ? "12px 8px" : "22px 12px",
                                            gap: "8px",
                                        }}
                                    >
                                        <img src={UploadRoundIcon} alt="Upload" style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px", filter: isFeaturedMember ? "none" : "brightness(0) invert(1)" }} />
                                        <p style={{
                                            color: isFeaturedMember ? theme.palette.primary.main : "#FFFFFF",
                                            fontSize: isMobile ? "12px" : "20px",
                                            fontWeight: 600,
                                            margin: 0,
                                            lineHeight: "1.3",
                                            textAlign: "center",
                                            whiteSpace: "normal",
                                        }}>
                                            {enumStrings.editMemberForm?.uploadBanner || enumStrings.manageMemberProfile.clicktoUpload}
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {/* Footer text + Preview Banners Button */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 0,
                    gap: "10px",
                    width: "100%",
                }}
            >
                {isFeaturedMember && (
                    <p
                        style={{
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "24px",
                            color: "#000000",
                            margin: 0,
                            textAlign: "center",
                            width: "100%",
                        }}
                        dangerouslySetInnerHTML={{ __html: footerText }}
                    />
                )}
                <Button
                    variant="contained"
                    style={{
                        backgroundColor: isFeaturedMember ? "#24272C" : "#888888",
                        color: "#FFFFFF",
                        fontSize: isMobile ? "16px" : "20px",
                        fontWeight: 700,
                        borderRadius: "12px",
                        padding: isMobile ? "14px 24px" : "15px 96px",
                        textTransform: "uppercase",
                        boxShadow: "none",
                        lineHeight: "150%",
                        width: isMobile ? "100%" : "auto",
                        ...(isFeaturedMember ? {} : { pointerEvents: "none" as const }),
                    }}
                    onClick={() => setBannerPreviewOpen(true)}
                >
                    {enumStrings.BannerPicker.previewBanners}
                </Button>
            </div>

            {/* Banner Upload Modal */}
            <BannerUploadModal
                open={bannerModalOpen}
                onClose={() => setBannerModalOpen(false)}
                bannerIndex={activeBannerIndex}
                initialData={(() => {
                    const activeBanner = formik.values.banner?.find((b: any) => b.position === activeBannerIndex);
                    return {
                        image: activeBanner?.contentType === "IMAGE" ? activeBanner?.contentUrl : null,
                        videoUrl: activeBanner?.contentType === "VIDEO" ? activeBanner?.contentUrl : null,
                        redirectUrl: activeBanner?.imageLink || null,
                    };
                })()}
                onSave={(data) => {
                    const currentBanners = [...(formik.values.banner || [])];

                    if (data.image || data.videoUrl) {
                        const newBanner = {
                            position: activeBannerIndex,
                            contentUrl: data.image || data.videoUrl || "",
                            contentType: data.image ? "IMAGE" as any : "VIDEO" as any,
                            imageLink: data.redirectUrl || null,
                        };

                        const existingIndex = currentBanners.findIndex(b => b.position === activeBannerIndex);
                        if (existingIndex >= 0) {
                            currentBanners[existingIndex] = newBanner;
                        } else {
                            currentBanners.push(newBanner);
                        }

                        currentBanners.sort((a, b) => a.position - b.position);
                        formik.setFieldValue("banner", currentBanners);
                    } else {
                        const filteredBanners = currentBanners.filter(b => b.position !== activeBannerIndex);
                        const repositionedBanners = filteredBanners.map((b, i) => ({ ...b, position: i + 1 }));
                        formik.setFieldValue("banner", repositionedBanners.length > 0 ? repositionedBanners : undefined);
                    }
                    setBannerModalOpen(false);
                }}
            />

            {/* Banner Preview Modal */}
            <BannerPreviewModal
                open={bannerPreviewOpen}
                onClose={() => setBannerPreviewOpen(false)}
                banners={(formik.values.banner || []).filter((b: any) => b?.contentUrl).map((b: any) => ({
                    image: b?.contentType === "IMAGE" ? b?.contentUrl || null : null,
                    videoUrl: b?.contentType === "VIDEO" ? b?.contentUrl || null : null,
                    redirectUrl: b?.imageLink || null,
                }))}
                phoneMockupImage={PerksBannerPhoneMockup}
            />

            {/* Delete Banner Confirmation Dialog */}
            <DeleteBannerModal
                open={deleteBannerPosition !== null}
                onClose={() => setDeleteBannerPosition(null)}
                onDelete={() => {
                    if (deleteBannerPosition !== null) {
                        const filteredBanners = (formik.values.banner || []).filter((b: any) => b.position !== deleteBannerPosition);
                        const repositionedBanners = filteredBanners.map((b: any, i: number) => ({ ...b, position: i + 1 }));
                        formik.setFieldValue("banner", repositionedBanners.length > 0 ? repositionedBanners : undefined);
                        setBannerPreviewOpen(false);
                        setDeleteBannerPosition(null);
                    }
                }}
            />
        </div>
    );
};

export default PerkBannerSection;
